import {UnstyledButton, createStyles} from '@mantine/core';
import React from 'react';

import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

function TemplateIcon(): JSX.Element {
  return (
    <svg
      fill="none"
      height="20"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width="20"
      aria-hidden="true"
    >
      <rect height="18" rx="2" ry="2" width="14" x="5" y="3" />
      <line x1="9" x2="15" y1="8" y2="8" />
      <line x1="9" x2="15" y1="12" y2="12" />
      <line x1="9" x2="12" y1="16" y2="16" />
    </svg>
  );
}

const useStyles = createStyles(() => ({
  root: {
    padding: '16px 16px 8px',
  },
  sectionHeader: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    color: '#868e96',
    marginBottom: 10,
  },
  cardIcon: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  cardTextWrapper: {
    overflow: 'hidden',
  },
  cardTitle: {
    fontSize: 14,
    fontWeight: 600,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  cardSubtitle: {
    fontSize: 12,
    color: '#868e96',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

interface QuickActionCardProps {
  icon: JSX.Element;
  iconBgColor: string;
  iconColor: string;
  title: string;
  subtitle: string;
  onClick: () => void;
}

function QuickActionCard({
  icon,
  iconBgColor,
  iconColor,
  title,
  subtitle,
  onClick,
}: QuickActionCardProps): JSX.Element {
  const {classes} = useStyles();
  return (
    <UnstyledButton
      onClick={onClick}
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: 12,
        borderRadius: theme.radius.md,
        border: `1px solid ${theme.colors.gray[2]}`,
        transition: 'all 100ms ease',
        '&:hover': {
          backgroundColor: theme.colors.gray[0],
          borderColor: theme.colors.gray[3],
        },
      })}
    >
      <div
        className={classes.cardIcon}
        style={{backgroundColor: iconBgColor, color: iconColor}}
        aria-hidden="true"
      >
        {icon}
      </div>
      <div className={classes.cardTextWrapper}>
        <div className={classes.cardTitle}>{title}</div>
        <div className={classes.cardSubtitle}>{subtitle}</div>
      </div>
    </UnstyledButton>
  );
}

interface OmniboxQuickActionsProps {
  composeEnabled: boolean;
  onEntryFromTemplate: () => void;
  onEntryFromCompose: () => void;
  onOpenReviews: () => void;
}

export default function OmniboxQuickActions({
  composeEnabled,
  onEntryFromTemplate,
  onEntryFromCompose,
  onOpenReviews,
}: OmniboxQuickActionsProps): JSX.Element {
  const {classes} = useStyles();
  const cardCount = composeEnabled ? 3 : 2;
  return (
    <div className={classes.root}>
      <div className={classes.sectionHeader}>Quick Actions</div>
      <div style={{display: 'grid', gridTemplateColumns: `repeat(${cardCount}, 1fr)`, gap: 10}}>
        <QuickActionCard
          icon={<TemplateIcon />}
          iconBgColor="#dbeafe"
          iconColor="#2563eb"
          title="Entry from Template"
          subtitle="Create new"
          onClick={onEntryFromTemplate}
        />
        {composeEnabled && (
          <QuickActionCard
            icon={<BenchlingIcon icon="sparkle" style={{fontSize: 20}} />}
            iconBgColor="#f3e8ff"
            iconColor="#7c3aed"
            title="Entry from Compose"
            subtitle="AI-assisted"
            onClick={onEntryFromCompose}
          />
        )}
        <QuickActionCard
          icon={<BenchlingIcon icon="done" style={{fontSize: 20}} />}
          iconBgColor="#dcfce7"
          iconColor="#16a34a"
          title="Pending Reviews"
          subtitle="View reviews"
          onClick={onOpenReviews}
        />
      </div>
    </div>
  );
}
